import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { api } from '@/lib/axios';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import { useInvalidateNotifications } from '@/hooks/use-invalidate-notifications';
import type { TeamResponse, InviteMemberPayload, InviteMemberResponse } from '@/types/team';

export const updateMemberRolesSchema = z.object({
  wholesalerId: z.string().min(1),
  updates: z.array(z.object({ userId: z.string().min(1), roleId: z.string().min(1) })).min(1),
});

export type UpdateMemberRolesPayload = z.infer<typeof updateMemberRolesSchema>;

export const updateMemberSchema = z.object({
  wholesalerId: z.string().min(1),
  userId: z.string().min(1),
  displayName: z.string().min(1),
  roleId: z.string().min(1),
});

export type UpdateMemberPayload = z.infer<typeof updateMemberSchema>;

export const fetchTeamMembers = async (wholesalerId: string): Promise<TeamResponse> => {
  const { data } = await api.get<TeamResponse>(API_ROUTES.team.members(wholesalerId));
  return data;
};

export const getTeamMembersQueryOptions = (wholesalerId: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.team.members(wholesalerId),
    queryFn: () => fetchTeamMembers(wholesalerId),
    staleTime: 0,
    refetchOnMount: 'always' as const,
  });

export const useTeamMembers = (wholesalerId: string | null) =>
  useQuery({
    ...getTeamMembersQueryOptions(wholesalerId ?? 'none'),
    enabled: wholesalerId != null,
  });

const inviteMember = async (payload: InviteMemberPayload): Promise<InviteMemberResponse> => {
  const { data } = await api.post<InviteMemberResponse>(
    API_ROUTES.team.invite(payload.wholesalerId),
    { email: payload.email },
  );
  return data;
};

export const useInviteMember = (wholesalerId: string) => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: InviteMemberPayload) => inviteMember(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getTeamMembersQueryOptions(wholesalerId).queryKey,
      });
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.teamPage.toast.inviteSent });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.teamPage.toast.inviteError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

const updateMemberRoles = async (payload: UpdateMemberRolesPayload): Promise<void> => {
  const validated = updateMemberRolesSchema.parse(payload);
  await api.patch(API_ROUTES.team.updateRoles(validated.wholesalerId), {
    updates: validated.updates,
  });
};

export const useUpdateMemberRoles = (wholesalerId: string) => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: UpdateMemberRolesPayload) => updateMemberRoles(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getTeamMembersQueryOptions(wholesalerId).queryKey,
      });
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.teamPage.toast.rolesUpdated });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.teamPage.toast.rolesError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

const updateMember = async (payload: UpdateMemberPayload): Promise<void> => {
  const validated = updateMemberSchema.parse(payload);
  await api.patch(API_ROUTES.team.updateMember(validated.wholesalerId, validated.userId), {
    displayName: validated.displayName,
    roleId: validated.roleId,
  });
};

export const useUpdateMember = (wholesalerId: string) => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: UpdateMemberPayload) => updateMember(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getTeamMembersQueryOptions(wholesalerId).queryKey,
      });
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.teamPage.toast.memberUpdated });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.teamPage.toast.memberUpdateError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
